import { useQuery } from "@tanstack/react-query";
import { fetchCurrentUser } from "../utils/fetchCurrentUser";
import type { CurrentUser } from "../types";
import { QUERY_CONFIG } from "../constants";

const QUERY_KEY = "currentUser";

const useCurrentUser = (): {
  error: unknown;
  isLoading: boolean;
  user?: CurrentUser;
} => {
  const {
    error,
    data,
    isLoading: isInitialLoading,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => fetchCurrentUser(),
    ...QUERY_CONFIG,
    // Auth state must self-correct when a session expires in an already-open
    // tab. Shorten the stale window so the QUERY_CONFIG window-focus refetch
    // actually fires, and poll while the tab is focused (refetchIntervalInBackground
    // defaults to false) so an idle-but-open tab still notices expiry.
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Retry once on failure
    retryDelay: 2000, // Wait 2 seconds before retrying
  });

  return {
    error,
    // Only show loading on initial fetch, not on background refetches (prevents form unmount)
    isLoading: isInitialLoading,
    user: data,
  };
};

export default useCurrentUser;
